import type { FillLogsPaperTarget, FillLogsTone } from './fill-logs-text';

const PULSE_MS = 1000;
const STILL_MARK_MS = 2000;
const SELECT_TRIGGER = '[data-testid="fill-multi-select-trigger"]';
const TONE_COLOR: Record<FillLogsTone, string> = {
  filled: '--color-change-added-border',
  corrected: '--color-change-fixed-border',
};
const UNCHANGED_COLOR = '--theme-secondary-ink';

export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

// Ids of added-row cells carry colons, so the lookup compares the dataset instead of building a
// selector that would need escaping.
function findByData(root: HTMLElement, attribute: 'fieldId' | 'fillField', ids: readonly string[]): HTMLElement[] {
  const wanted = new Set(ids);
  const selector = attribute === 'fieldId' ? '[data-field-id]' : '[data-fill-field]';
  return [...root.querySelectorAll<HTMLElement>(selector)].filter((element) =>
    wanted.has(element.dataset[attribute] ?? '')
  );
}

export function findByFieldId(root: HTMLElement, ids: readonly string[]): HTMLElement[] {
  return findByData(root, 'fieldId', ids);
}

function cssColor(element: HTMLElement, token: string): string {
  return getComputedStyle(element).getPropertyValue(token).trim() || 'currentColor';
}

// The paper belongs to the renderer, so the mark is an animation on its element instead of a class
// the next render of the renderer would wipe. The focus goes to the field wrapper, the one stop a
// read-only field offers; the renderer can only name it after the field id when the matrix leaves
// the label blank, so the name the list resolved replaces it.
export function pulseOnPaper(paper: HTMLElement, target: FillLogsPaperTarget): boolean {
  const controls = findByFieldId(paper, target.targets).map(
    (element) => element.querySelector<HTMLElement>(SELECT_TRIGGER) ?? element,
  );
  const [wrapper] = findByData(paper, 'fillField', target.targets.slice(0, 1));
  if (controls.length === 0 && !wrapper) return false;

  const reduced = prefersReducedMotion();
  const first = wrapper ?? controls[0];
  first.scrollIntoView?.({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
  if (wrapper) {
    if (target.label) wrapper.setAttribute('aria-label', target.label);
    wrapper.focus({ preventScroll: true });
  }
  for (const control of controls) animate(control, target.tone, reduced);
  return true;
}

function animate(control: HTMLElement, tone: FillLogsTone | null, reduced: boolean): void {
  if (typeof control.animate !== 'function') return;
  const color = cssColor(control, tone ? TONE_COLOR[tone] : UNCHANGED_COLOR);
  if (reduced) {
    const still = { outline: `3px solid ${color}`, outlineOffset: '2px' };
    control.animate([still, still], { duration: STILL_MARK_MS });
  } else {
    control.animate([{ boxShadow: `0 0 0 0 ${color}` }, { boxShadow: '0 0 0 10px transparent' }], {
      duration: PULSE_MS,
      iterations: 2,
      easing: 'ease-out',
    });
  }
}
